import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Md } from '@/components/Md';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtDate } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const RATINGS = [
  { value: 1, label: 'Muito insatisfeita', emoji: '😞' },
  { value: 2, label: 'Insatisfeita', emoji: '😕' },
  { value: 3, label: 'Neutra', emoji: '😐' },
  { value: 4, label: 'Satisfeita', emoji: '🙂' },
  { value: 5, label: 'Muito satisfeita', emoji: '😊' },
];
const PROGRAMS = ['', 'gravida', 'pos_parto', 'pre_concecao', 'introducao_alimentar', 'geral', 'emagrecimento'];
const PROG_LABELS = { gravida: 'Super Barriguinhas', pos_parto: 'Super Mamãs', pre_concecao: 'Super Pré Mamãs', introducao_alimentar: 'Super Bebés', geral: 'Geral', emagrecimento: 'Geral' };

export default function AdminFeedbackPage() {
  useRequireAdmin();
  const [feedbacks, setFeedbacks] = useState([]);
  const [filterProg, setFilterProg] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterProg) params.set('program', filterProg);
      if (filterRating) params.set('rating', filterRating);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await axios.get(`${API}/api/admin/consultation-feedback?${params}`);
      setFeedbacks(data);
    } catch {}
  }, [filterProg, filterRating, dateFrom, dateTo]);
  useEffect(() => { fetch(); }, [fetch]);

  const getRating = (v) => RATINGS.find(r => r.value === v) || RATINGS[2];
  const avgRating = feedbacks.length ? (feedbacks.reduce((s, f) => s + f.rating, 0) / feedbacks.length).toFixed(1) : '-';
  const positivePct = feedbacks.length ? Math.round(feedbacks.filter(f => f.rating >= 4).length / feedbacks.length * 100) : 0;
  const avg3 = (f) => {
    const vals = [f.plan_speed, f.tech_quality, f.materials_useful].filter(v => v != null);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };
  const ScoreCell = ({ label, value, highlight }) => (
    <div className={`rounded-xl px-2 py-1.5 text-center ${highlight ? 'bg-brand-green/10' : 'bg-brand-bg/40'}`}>
      <p className={`text-sm font-semibold ${highlight ? 'text-brand-green' : 'text-brand-text'}`}>{value != null ? value : '–'}</p>
      <p className="text-[9px] text-brand-muted leading-tight">{label}</p>
    </div>
  );

  return (
    <div className="fade-in" data-testid="admin-feedback-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Feedback de Consultas</h2>
          <p className="text-xs text-brand-muted mt-0.5">{feedbacks.length} feedbacks recebidos</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-heading font-bold text-brand-green">{avgRating}</p>
          <p className="text-[10px] text-brand-muted">Média (1-5)</p>
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-heading font-bold text-brand-text">{feedbacks.length}</p>
          <p className="text-[10px] text-brand-muted">Total</p>
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-heading font-bold text-brand-green">{positivePct}%</p>
          <p className="text-[10px] text-brand-muted">Positivos</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <select value={filterProg} onChange={e => setFilterProg(e.target.value)} className="border border-brand-border rounded-xl px-3 py-1.5 text-xs bg-white">
            <option value="">Todos os programas</option>
            {PROGRAMS.filter(Boolean).map(p => <option key={p} value={p}>{PROG_LABELS[p] || p}</option>)}
          </select>
          <select value={filterRating} onChange={e => setFilterRating(e.target.value)} className="border border-brand-border rounded-xl px-3 py-1.5 text-xs bg-white">
            <option value="">Todas as classificações</option>
            {RATINGS.map(r => <option key={r.value} value={r.value}>{r.emoji} {r.label}</option>)}
          </select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-xl border-brand-border text-xs w-32" placeholder="De" />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-xl border-brand-border text-xs w-32" placeholder="Até" />
        </div>
      </div>

      {/* Table */}
      {feedbacks.length === 0 ? (
        <p className="text-center text-brand-muted py-8">Sem feedbacks para os filtros seleccionados.</p>
      ) : (
        <div className="space-y-2">
          {feedbacks.map(f => {
            const r = getRating(f.rating);
            return (
              <div key={f.id} className={`bg-white border rounded-2xl p-4 ${f.rating <= 2 ? 'border-red-200 bg-red-50/20' : 'border-brand-border'}`} data-testid={`feedback-${f.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{r.emoji}</span>
                      <span className="text-sm font-medium text-brand-text">{f.patient_name}</span>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full ${f.rating >= 4 ? 'bg-green-50 text-green-600' : f.rating <= 2 ? 'bg-red-50 text-red-500' : 'bg-brand-bg text-brand-muted'}`}>{r.label}</span>
                    </div>
                    <div className="flex gap-2 text-[10px] text-brand-muted">
                      <span>{fmtDate(f.consultation_date || f.created_at)}</span>
                      <span>{PROG_LABELS[f.patient_type] || f.patient_type}</span>
                    </div>
                  </div>
                </div>
                {f.comment && <Md as="p" className="text-xs text-brand-muted mt-2 bg-brand-bg/20 rounded-xl p-2">{f.comment}</Md>}
                {(f.plan_speed != null || f.tech_quality != null || f.materials_useful != null) && (
                  <div className="mt-2 grid grid-cols-4 gap-2" data-testid={`feedback-scores-${f.id}`}>
                    <ScoreCell label="Rapidez plano" value={f.plan_speed} />
                    <ScoreCell label="Qualidade técnica" value={f.tech_quality} />
                    <ScoreCell label="Materiais úteis" value={f.materials_useful} />
                    <ScoreCell label="Média (0–10)" value={avg3(f)} highlight />
                  </div>
                )}
                {f.menu_opinion && (
                  <div className="mt-2 bg-brand-mint/10 rounded-xl p-2" data-testid={`feedback-menu-${f.id}`}>
                    <p className="text-[10px] text-brand-muted">Opinião sobre a ementa do bebé</p>
                    <p className="text-xs text-brand-text">{f.menu_opinion}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
